import type { Funnel, Lead, Membership, Stage } from "../../lib/types";
import {
  flowNodeTypeLabels,
} from "../flows/editor";
import type {
  FlowActiveVersion,
  FlowDraft,
  FlowNode,
  FlowNodeType,
} from "../flows/contracts";
import { deltaLabel, growth, type GrowthResult } from "../leads/list";

/**
 * Regras da relação de Funis e do editor de processo (protótipo Zênite,
 * `#page-funis` e `#zProcessEditor`) sobre o domínio real: funil, etapas,
 * participações, rascunhos de flow e versões ativas.
 *
 * Nada aqui decide interface; a página consome estes cálculos e o protótipo
 * define a composição. Estados que o domínio ainda não oferece (pausar
 * inscrições, reuniões e lembretes como blocos) ficam explícitos como
 * indisponíveis, nunca simulados (AGENTS §3, §18, §27.6).
 */

export type HubView = "all" | "on" | "paused" | "draft";
export type HubConfig = "all" | "ready" | "pending";
export type HubStatus = "on" | "paused" | "draft";
export type HubSortKey = "name" | "status" | "members" | "stages" | "updatedAt";

export const HUB_VIEWS: ReadonlyArray<{ id: HubView; label: string }> = [
  { id: "all", label: "Todos" },
  { id: "on", label: "Ativos" },
  { id: "paused", label: "Pausados" },
  { id: "draft", label: "Rascunhos" },
];

export const HUB_CONFIG_OPTIONS: ReadonlyArray<{ value: HubConfig; label: string }> = [
  { value: "all", label: "Todas" },
  { value: "ready", label: "Completa" },
  { value: "pending", label: "Pendente" },
];

export const HUB_STATUS_LABELS: Record<HubStatus, string> = {
  on: "Ativo",
  paused: "Pausado",
  draft: "Rascunho",
};

export interface HubRow {
  funnel: Funnel;
  /** Definição de processo do funil: um rascunho por funil nesta fase. */
  draft?: FlowDraft;
  active?: FlowActiveVersion;
  status: HubStatus;
  blocks: number;
  stages: number;
  /** Cadastros únicos, não arquivados, com participação neste funil. */
  participants: number;
  participantIds: string[];
  /** Sem rascunho ou sem blocos: "Configuração pendente". */
  pending: boolean;
  /** Última alteração registrada (o rascunho); funil sem rascunho não tem data. */
  updatedAt?: string;
  /** Segunda linha da identidade: descrição do funil ou o estado do processo. */
  note: string;
  /** Motivo pelo qual o interruptor Ativar/Pausar está indisponível. */
  toggleReason: string;
}

export interface HubInput {
  funnels: Funnel[];
  stages: Stage[];
  leads: Lead[];
  memberships: Membership[];
  flowDrafts?: FlowDraft[];
  flowActiveVersions?: FlowActiveVersion[];
}

export const HUB_TOGGLE_REASON_DRAFT = "Publique uma versão demo antes de ativar ou pausar.";
/** Pausar interrompe novas inscrições; a versão publicada continua íntegra. */
export const HUB_TOGGLE_REASON_ON = "Pausar as inscrições deste funil";
export const HUB_TOGGLE_REASON_PAUSED = "Retomar as inscrições deste funil";

/** O rascunho editável do funil (o de entrada quando houver mais de um). */
export function funnelDraft(drafts: FlowDraft[] | undefined, funnelId: string): FlowDraft | undefined {
  const own = (drafts ?? []).filter((draft) => draft.funnelId === funnelId && draft.status === "editable");
  return own.find((draft) => draft.isEntryFlow) ?? own[0];
}

export function funnelParticipantIds(input: Pick<HubInput, "leads" | "memberships">, funnelId: string): string[] {
  const alive = new Set(input.leads.filter((lead) => !lead.archived).map((lead) => lead.id));
  const ids = new Set<string>();
  for (const membership of input.memberships) {
    if (membership.funnelId === funnelId && alive.has(membership.leadId)) ids.add(membership.leadId);
  }
  return [...ids];
}

export function hubRows(input: HubInput): HubRow[] {
  return input.funnels
    .filter((funnel) => !funnel.archived)
    .map((funnel) => {
      const draft = funnelDraft(input.flowDrafts, funnel.id);
      const active = draft
        ? (input.flowActiveVersions ?? []).find((item) => item.flowId === draft.id)
        : undefined;
      const blocks = draft?.nodes.length ?? 0;
      const stages = input.stages.filter((stage) => stage.funnelId === funnel.id && !stage.archived).length;
      const participantIds = funnelParticipantIds(input, funnel.id);
      const pending = blocks === 0;
      const status: HubStatus = active ? (active.pausedAt ? "paused" : "on") : "draft";
      const description = funnel.description.trim();
      const note = description
        ? description
        : pending
          ? "Etapas de CRM configuradas · processo a definir"
          : `Processo em rascunho · ${blocks} ${blocks === 1 ? "bloco" : "blocos"}`;
      return {
        funnel,
        draft,
        active,
        status,
        blocks,
        stages,
        participants: participantIds.length,
        participantIds,
        pending,
        updatedAt: draft?.updatedAt,
        note,
        toggleReason: !active
          ? HUB_TOGGLE_REASON_DRAFT
          : active.pausedAt
            ? HUB_TOGGLE_REASON_PAUSED
            : HUB_TOGGLE_REASON_ON,
      };
    });
}

export function matchesHubView(row: HubRow, view: HubView): boolean {
  if (view === "all") return true;
  return row.status === view;
}

export function matchesHubConfig(row: HubRow, config: HubConfig): boolean {
  if (config === "all") return true;
  return config === "pending" ? row.pending : !row.pending;
}

export function matchesHubSearch(row: HubRow, query: string): boolean {
  const q = query.trim().toLocaleLowerCase("pt-BR");
  if (!q) return true;
  return (
    row.funnel.name.toLocaleLowerCase("pt-BR").includes(q) ||
    row.funnel.description.toLocaleLowerCase("pt-BR").includes(q) ||
    row.note.toLocaleLowerCase("pt-BR").includes(q)
  );
}

const STATUS_ORDER: Record<HubStatus, number> = { on: 0, paused: 1, draft: 2 };

export function sortHubRows(rows: HubRow[], key: HubSortKey, direction: "asc" | "desc"): HubRow[] {
  const sign = direction === "asc" ? 1 : -1;
  const collator = new Intl.Collator("pt-BR", { sensitivity: "base" });
  return [...rows].sort((a, b) => {
    let result = 0;
    switch (key) {
      case "name":
        result = collator.compare(a.funnel.name, b.funnel.name);
        break;
      case "status":
        result = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        break;
      case "members":
        result = a.participants - b.participants;
        break;
      case "stages":
        result = a.blocks - b.blocks || a.stages - b.stages;
        break;
      case "updatedAt":
        result = (a.updatedAt ?? "").localeCompare(b.updatedAt ?? "");
        break;
    }
    return result * sign || collator.compare(a.funnel.name, b.funnel.name);
  });
}

export interface HubSummary {
  participants: number;
  funnels: number;
  active: number;
  attention: number;
  growth: {
    participants: GrowthResult;
    funnels: GrowthResult;
    active: GrowthResult;
    attention: GrowthResult;
  };
}

/**
 * Os quatro indicadores do topo, sobre as linhas filtradas (todas as páginas).
 * Participantes conta cadastros únicos entre os funis; a variação usa a data
 * de criação do cadastro. Os demais usam a última alteração do rascunho;
 * funil sem rascunho não entra no comparativo.
 */
export function hubSummary(rows: HubRow[], leads: Lead[], now: string): HubSummary {
  const leadById = new Map(leads.map((lead) => [lead.id, lead]));
  const participantIds = new Set(rows.flatMap((row) => row.participantIds));
  const participantDates = [...participantIds].map((id) => leadById.get(id)?.createdAt);
  const activeRows = rows.filter((row) => row.status === "on");
  const attentionRows = rows.filter((row) => row.pending);
  return {
    participants: participantIds.size,
    funnels: rows.length,
    active: activeRows.length,
    attention: attentionRows.length,
    growth: {
      participants: growth(participantDates, now),
      funnels: growth(rows.map((row) => row.updatedAt), now),
      active: growth(activeRows.map((row) => row.updatedAt), now),
      attention: growth(attentionRows.map((row) => row.updatedAt), now),
    },
  };
}

/** Rodapé do indicador: o protótipo mostra "↔0%" quando não há base nem variação. */
export function hubDeltaLabel(result: GrowthResult, format: (value: number) => string): string {
  if (result.current === 0 && result.before === 0) return "↔0%";
  return deltaLabel(result, format);
}

export function hubUpdatedLabel(iso: string | undefined): string {
  if (!iso) return "Não registrada";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Não registrada";
  return date.toLocaleDateString("pt-BR", { day: "numeric", month: "short" });
}

// --- Etapas ------------------------------------------------------------------

/** Tom por posição da etapa, como o protótipo colore esteira, chips e pílulas. */
export const STAGE_TONES = [
  "var(--accent)",
  "var(--accent-2)",
  "var(--warn)",
  "var(--cx-pink)",
  "var(--good)",
] as const;

export function stageTone(index: number): string {
  return STAGE_TONES[((index % STAGE_TONES.length) + STAGE_TONES.length) % STAGE_TONES.length];
}

export const STAGE_TEMPLATES: ReadonlyArray<{ id: string; label: string; stages: string[] }> = [
  { id: "b2b", label: "Vendas B2B", stages: ["Novo", "Qualificação", "Reunião", "Proposta", "Fechamento"] },
  { id: "inbound", label: "Inbound", stages: ["Lead", "Contato", "Diagnóstico", "Proposta", "Ganho"] },
  { id: "pos", label: "Pós-venda", stages: ["Onboarding", "Ativo", "Renovação", "Expansão"] },
  { id: "parc", label: "Parcerias", stages: ["Mapeado", "Conversa", "Acordo", "Ativo"] },
];

export const DEFAULT_STAGE_NAMES = STAGE_TEMPLATES[0].stages;

/** Nomes normalizados, sem vazios e sem repetição (comparação sem caixa). */
export function normalizeStageNames(names: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of names) {
    const name = raw.trim().replace(/\s+/g, " ");
    if (!name) continue;
    const key = name.toLocaleLowerCase("pt-BR");
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(name);
  }
  return result;
}

// --- Blocos: paleta e apresentação -----------------------------------------

export type PaletteGroup = "Entrada" | "Comunicação" | "Lógica" | "Operação";

export interface PaletteItem {
  kind: string;
  group: PaletteGroup;
  label: string;
  hint: string;
  tone: string;
  /** Tipo de nó do domínio que o bloco cria; ausente quando o bloco ainda não existe no contrato. */
  type?: FlowNodeType;
  /** Variante do nó (tarefa comum ou revisão para a operação). */
  taskActionType?: "human_task" | "review";
  /** Motivo pelo qual o bloco fica visível e desabilitado (§18). */
  unavailable?: string;
}

export const PALETTE: readonly PaletteItem[] = [
  { kind: "trigger", group: "Entrada", label: "Entrada do cadastro", hint: "Cadastro manual, formulário ou transferência", tone: "var(--accent)", type: "start" },
  { kind: "action", group: "Comunicação", label: "Enviar conteúdo", hint: "Mensagem ou material de apoio", tone: "var(--good)", type: "send_message" },
  { kind: "meeting", group: "Comunicação", label: "Reunião", hint: "Encontros e participantes da negociação", tone: "var(--accent)", type: "meeting" },
  { kind: "billing", group: "Comunicação", label: "Lembrete de mensalidade", hint: "Prévia por sistema, e-mail e WhatsApp", tone: "var(--warn)", type: "billing" },
  { kind: "wait", group: "Lógica", label: "Espera", hint: "Prazo antes da próxima ação", tone: "var(--accent-2)", type: "wait" },
  { kind: "cond", group: "Lógica", label: "Condição", hint: "Dois caminhos com saídas nomeadas", tone: "var(--warn)", type: "condition" },
  { kind: "task", group: "Operação", label: "Criar tarefa", hint: "Próxima ação ligada ao cadastro", tone: "var(--accent)", type: "human_task", taskActionType: "human_task" },
  { kind: "stage", group: "Operação", label: "Atualizar etapa", hint: "Posição na esteira de CRM", tone: "var(--good)", type: "stage" },
  { kind: "notice", group: "Operação", label: "Notificar operação", hint: "Pendência com motivo e prazo", tone: "var(--warn)", type: "human_task", taskActionType: "review" },
  { kind: "transfer", group: "Operação", label: "Transferir participação", hint: "Encerra passos pendentes da origem", tone: "var(--accent-2)", type: "call_flow" },
  { kind: "end", group: "Operação", label: "Encerrar percurso", hint: "Preserva o histórico da participação", tone: "var(--muted)", type: "end" },
];

export const PALETTE_GROUPS: readonly PaletteGroup[] = ["Entrada", "Comunicação", "Lógica", "Operação"];

export interface NodePresentation {
  kicker: string;
  tone: string;
  group: PaletteGroup;
}

/** Como o bloco aparece no canvas e no inspetor: rótulo de tipo e tom. */
export function nodePresentation(node: Pick<FlowNode, "type" | "config">): NodePresentation {
  switch (node.type) {
    case "start":
      return { kicker: "Entrada do cadastro", tone: "var(--accent)", group: "Entrada" };
    case "send_message":
      return { kicker: "Enviar conteúdo", tone: "var(--good)", group: "Comunicação" };
    case "meeting":
      return { kicker: "Reunião", tone: "var(--accent)", group: "Comunicação" };
    case "billing":
      return { kicker: "Lembrete de mensalidade", tone: "var(--warn)", group: "Comunicação" };
    case "wait":
      return { kicker: "Espera", tone: "var(--accent-2)", group: "Lógica" };
    case "condition":
      return { kicker: "Condição", tone: "var(--warn)", group: "Lógica" };
    case "human_task":
      return node.config.type === "human_task" && node.config.taskActionType === "review"
        ? { kicker: "Notificar operação", tone: "var(--warn)", group: "Operação" }
        : { kicker: "Criar tarefa", tone: "var(--accent)", group: "Operação" };
    case "stage":
      return { kicker: "Atualizar etapa", tone: "var(--good)", group: "Operação" };
    case "call_flow":
      return { kicker: "Transferir participação", tone: "var(--accent-2)", group: "Operação" };
    case "end":
      return { kicker: "Encerrar percurso", tone: "var(--muted)", group: "Operação" };
    case "won":
      return { kicker: flowNodeTypeLabels.won, tone: "var(--good)", group: "Operação" };
    case "lost":
      return { kicker: flowNodeTypeLabels.lost, tone: "var(--crit)", group: "Operação" };
    case "random_split":
      return { kicker: flowNodeTypeLabels.random_split, tone: "var(--warn)", group: "Lógica" };
    case "ai_response":
      return { kicker: flowNodeTypeLabels.ai_response, tone: "var(--accent-2)", group: "Comunicação" };
    case "set_field_or_tag":
      return { kicker: flowNodeTypeLabels.set_field_or_tag, tone: "var(--muted)", group: "Operação" };
  }
}

export const WAIT_OPTIONS: ReadonlyArray<{ value: string; label: string; minutes: number }> = [
  { value: "60", label: "1 hora", minutes: 60 },
  { value: "240", label: "4 horas", minutes: 240 },
  { value: "1440", label: "1 dia", minutes: 1440 },
  { value: "2880", label: "2 dias", minutes: 2880 },
  { value: "4320", label: "3 dias", minutes: 4320 },
  { value: "10080", label: "7 dias", minutes: 10080 },
  { value: "20160", label: "14 dias", minutes: 20160 },
];

export function waitLabel(minutes: number): string {
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} ${days === 1 ? "dia" : "dias"}`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? "hora" : "horas"}`;
  }
  return `${minutes} min`;
}

/** Texto livre que o bloco guarda de fato; os demais não têm instrução própria. */
export function nodeInstruction(node: FlowNode): string | null {
  switch (node.config.type) {
    case "human_task":
      return node.config.instruction;
    case "end":
      return node.config.outcomeNote ?? "";
    case "lost":
      return node.config.evidence;
    default:
      return null;
  }
}

export function withNodeInstruction(node: FlowNode, instruction: string): FlowNode {
  switch (node.config.type) {
    case "human_task":
      return { ...node, config: { ...node.config, instruction } };
    case "end":
      return { ...node, config: { ...node.config, outcomeNote: instruction } };
    case "lost":
      return { ...node, config: { ...node.config, evidence: instruction } };
    default:
      return node;
  }
}

/** Posição do próximo bloco: à direita do último, como o protótipo organiza. */
export function nextNodePosition(nodes: FlowNode[]): { x: number; y: number } {
  if (nodes.length === 0) return { x: 0, y: 0 };
  const maxX = Math.max(...nodes.map((node) => node.position?.x ?? 0));
  const rightmost = nodes.filter((node) => (node.position?.x ?? 0) === maxX);
  const y = Math.min(...rightmost.map((node) => node.position?.y ?? 0));
  return { x: maxX + 360, y };
}

/** Organiza por profundidade a partir das entradas: coluna = distância, linha = ordem. */
export function arrangeNodes(nodes: FlowNode[], edges: Array<{ fromNodeId: string; toNodeId: string }>): FlowNode[] {
  const incoming = new Map(nodes.map((node) => [node.id, 0]));
  for (const edge of edges) incoming.set(edge.toNodeId, (incoming.get(edge.toNodeId) ?? 0) + 1);
  const roots = nodes.filter((node) => node.type === "start" || (incoming.get(node.id) ?? 0) === 0);
  const depth = new Map<string, number>();
  const queue = roots.map((node) => node.id);
  for (const id of queue) depth.set(id, 0);
  while (queue.length) {
    const id = queue.shift()!;
    for (const edge of edges) {
      if (edge.fromNodeId !== id || depth.has(edge.toNodeId)) continue;
      depth.set(edge.toNodeId, (depth.get(id) ?? 0) + 1);
      queue.push(edge.toNodeId);
    }
  }
  for (const node of nodes) if (!depth.has(node.id)) depth.set(node.id, 0);
  const rowsByDepth = new Map<number, number>();
  return nodes.map((node) => {
    const column = depth.get(node.id) ?? 0;
    const row = rowsByDepth.get(column) ?? 0;
    rowsByDepth.set(column, row + 1);
    return { ...node, position: { x: column * 360, y: row * 210 } };
  });
}
